'use client';

import {
  Button,
  cn,
  DEFAULT_PROFILE_ACCENT,
  Input,
  Label,
  normalizeProfileAccentColor,
  PROFILE_CTA_WHITE,
  resolveProfileCtaColors,
  resolveProfileTokens,
} from '@up2stay/up2promo-ui';
import { Info } from 'lucide-react';
import { useState } from 'react';

import {
  buildProfileStylingPayload,
  stylingToFormSlice,
  type ProfileStylingFormSlice,
} from '@/core/lib/profile-styling-form';

export type ProfileAccentColorFieldLabels = {
  label: string;
  description: string;
  reset: string;
  placeholder: string;
  /** Profile CTA preview (DEV-790). Omit on non-profile surfaces. */
  ctaPreview?: ProfileAccentCtaPreviewLabels;
};

export type ProfileAccentCtaPreviewLabels = {
  /** Sample CTA label, e.g. the profile's "Kontakt speichern". */
  ctaLabel: string;
  preview: string;
  adjustedDarker: string;
  adjustedLighter: string;
};

type ProfileAccentColorFieldProps = {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  showHeader?: boolean;
  labels: ProfileAccentColorFieldLabels;
};

export function ProfileAccentColorField({
  value,
  onChange,
  disabled = false,
  showHeader = true,
  labels,
}: ProfileAccentColorFieldProps) {
  const normalized = normalizeProfileAccentColor(value);
  const pickerValue = normalized ?? DEFAULT_PROFILE_ACCENT;
  const [hexInput, setHexInput] = useState(value ?? '');
  const [prevValue, setPrevValue] = useState(value);

  if (value !== prevValue) {
    setPrevValue(value);
    setHexInput(value ?? '');
  }

  const isCustom = Boolean(normalized);

  const commitHex = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      onChange(null);
      return;
    }
    const candidate = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    const next = normalizeProfileAccentColor(candidate);
    if (next) {
      onChange(next);
      setHexInput(next);
    } else {
      setHexInput(value ?? '');
    }
  };

  return (
    <div className={cn(showHeader ? 'space-y-3 pt-2' : 'space-y-3')}>
      {showHeader ? (
        <div className="flex items-center gap-0.5">
          <Label>{labels.label}</Label>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative inline-flex shrink-0">
          <span className="sr-only">{labels.label}</span>
          <input
            type="color"
            value={pickerValue}
            disabled={disabled}
            onChange={(event) => {
              const next = normalizeProfileAccentColor(event.target.value);
              if (next) {
                onChange(next);
                setHexInput(next);
              }
            }}
            className={cn(
              'h-10 w-10 cursor-pointer appearance-none overflow-hidden rounded-md border border-input bg-transparent p-0',
              'disabled:cursor-not-allowed disabled:opacity-50',
              '[&::-webkit-color-swatch-wrapper]:p-0',
              '[&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch]:rounded-[calc(0.375rem-1px)]',
              '[&::-moz-color-swatch]:border-none [&::-moz-color-swatch]:rounded-[calc(0.375rem-1px)]',
            )}
          />
        </label>
        <Input
          value={hexInput}
          placeholder={labels.placeholder}
          aria-label={`${labels.label} (Hex)`}
          disabled={disabled}
          onChange={(event) => setHexInput(event.target.value)}
          onBlur={() => commitHex(hexInput)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitHex(hexInput);
              event.currentTarget.blur();
            }
          }}
          className="max-w-[8.5rem] font-mono"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || !isCustom}
          onClick={() => {
            onChange(null);
            setHexInput('');
          }}
        >
          {labels.reset}
        </Button>
      </div>
      {labels.ctaPreview ? (
        <ProfileAccentCtaPreview
          accentColor={pickerValue}
          labels={labels.ctaPreview}
        />
      ) : null}
    </div>
  );
}

/** Live profile CTA on a light profile surface; never blocks saving. */
function ProfileAccentCtaPreview({
  accentColor,
  labels,
}: {
  accentColor: string;
  labels: ProfileAccentCtaPreviewLabels;
}) {
  const cta = resolveProfileCtaColors(accentColor);
  const surface = resolveProfileTokens(accentColor, 'light').bg;
  const hint = !cta.shifted
    ? null
    : cta.text === PROFILE_CTA_WHITE
      ? labels.adjustedDarker
      : labels.adjustedLighter;

  return (
    <div className="space-y-2">
      <div
        className="flex flex-wrap items-center gap-3 rounded-[10px] p-2.5"
        style={{ background: surface }}
      >
        <span
          className="inline-flex min-h-10 items-center justify-center rounded-xl px-[18px] text-[13px] font-semibold"
          style={{
            backgroundImage: `linear-gradient(135deg, ${cta.fill}, ${cta.end})`,
            color: cta.text,
          }}
        >
          {labels.ctaLabel}
        </span>
        <span className="text-[12.5px] text-[#5f6878]">{labels.preview}</span>
      </div>
      {hint ? (
        <p className="flex items-start gap-1.5 text-[12.5px] text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>{hint}</span>
        </p>
      ) : null}
    </div>
  );
}

export function buildProfileStylingWithAccent(
  existing: Parameters<typeof stylingToFormSlice>[0],
  accentColor: string | null | undefined,
): ReturnType<typeof buildProfileStylingPayload> {
  const slice: ProfileStylingFormSlice = stylingToFormSlice(existing);
  slice.accentColor = accentColor ?? null;
  return buildProfileStylingPayload(slice);
}
